/**
 * SourcingPanel — Sourcing Command Center (3-pane, messaging-app layout).
 *   LEFT  : pipeline rail (suppliers grouped by status)
 *   CENTER: unified multi-channel conversation (email/WhatsApp/Alibaba) + AI co-pilot
 *   RIGHT : manufacturer details + actions (what they make, website, factory,
 *           quotes/award, sample, escalate, save to library)
 * Fixed-height region; each pane scrolls internally with modern thin scrollbars.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Plus, Send, Sparkles, Mail, MessageCircle, Globe, Star,
  AlertTriangle, Zap, Package, MapPin, Users as UsersIcon, ExternalLink, FlaskConical, Paperclip, Loader2, Upload,
} from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';
import Markdown from '../../../components/Markdown';

const STATUS = {
  attention: { label: 'Needs you', color: '#d97706' },
  replied: { label: 'Replied', color: '#0891b2' },
  quoted: { label: 'Quoted', color: '#059669' },
  listed: { label: 'Listing', color: '#0d9488' },
  contacted: { label: 'Contacted', color: '#7c3aed' },
  shortlisted: { label: 'Shortlisted', color: '#2563eb' },
  awarded: { label: 'Awarded', color: '#15803d' },
  discovered: { label: 'Discovered', color: '#6b7785' },
  rejected: { label: 'Rejected', color: '#9aa4b2' },
};
const GROUP_ORDER = ['attention', 'replied', 'quoted', 'listed', 'contacted', 'shortlisted', 'discovered', 'awarded', 'rejected'];
// Thread.state (set by Accio via log_negotiation) → friendly label + colour.
const STATE_LABEL = {
  awaiting_supplier: ['Awaiting supplier', '#d97706'], awaiting_us: ['Replied — your turn', '#0891b2'],
  needs_human: ['Needs review', '#b91c1c'], quoted: ['Quoted', '#059669'],
  negotiating: ['Negotiating', '#7c3aed'], closed: ['Closed', '#6b7785'], new: ['Not contacted', '#6b7785'],
};
const CHAN = { email: { icon: Mail, label: 'Email' }, whatsapp: { icon: MessageCircle, label: 'WhatsApp' }, alibaba: { icon: Globe, label: 'Alibaba' } };
const AV = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#db2777', '#475569'];
const money = (q) => (q?.unit_price ?? q?.price);
const avColor = (s) => AV[[...(s || '?')].reduce((a, c) => a + c.charCodeAt(0), 0) % AV.length];
const initials = (s) => (s || '?').replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
const ChanIcon = ({ ch, size = 12 }) => { const C = (CHAN[ch] || CHAN.email).icon; return <C size={size} />; };

const SourcingPanel = ({ hideAccio = false }) => {
  const { tenderId, summary, refresh } = useForge();
  const candidates = summary?.shortlist || [];
  const threads = summary?.inbox || [];
  const specLines = summary?.spec_sheet?.line_items || [];

  const [quotesBy, setQuotesBy] = useState({});
  const [docsBy, setDocsBy] = useState({});   // supplier evidence docs by candidate_id
  const [selectedId, setSelectedId] = useState(null);
  const [channel, setChannel] = useState('email');
  const [draft, setDraft] = useState('');
  const [autopilot, setAutopilot] = useState(false);
  const [busy, setBusy] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverErr, setDiscoverErr] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ company: '', country: '', email: '' });
  const [persona, setPersona] = useState('standard');
  const [simBusy, setSimBusy] = useState(false);
  const threadEnd = useRef(null);

  // Link a candidate to its thread. Accio keys candidates (alibaba:<short>) and
  // negotiation threads (accio-<slug>) with DIFFERENT id schemes, so fall back to
  // matching on the normalised company name (as quotesFor does).
  const normCo = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const threadFor = (c) => {
    if (!c) return undefined;
    return threads.find((t) => t.supplier_id === c.candidate_id)
      || (c.company ? threads.find((t) => normCo(t.supplier_name) === normCo(c.company)) : undefined);
  };
  const quotesFor = (c) => quotesBy[c?.candidate_id] || quotesBy[c?.company] || [];
  // Flatten quotes -> one row per line item (quotes store nested line_items[]).
  const quoteRows = (c) => quotesFor(c).flatMap((q) =>
    (q.line_items && q.line_items.length
      ? q.line_items.map((li) => ({
          quote_id: q.quote_id, line_item: li.line_item, unit_price: li.unit_price,
          currency: li.currency || 'USD', moq: li.moq, lead: li.lead_time_weeks,
          quantity: li.quantity, awarded: q.awarded || q.selected, sample: q.sample,
        }))
      : [{ quote_id: q.quote_id, line_item: q.line_item, unit_price: q.unit_price ?? q.price,
           moq: q.moq, awarded: q.awarded || q.selected }]));

  const statusOf = useCallback((c) => {
    const t = threadFor(c); const q = quotesFor(c);
    if (c.status === 'rejected') return 'rejected';
    if (q.some((x) => x.selected || x.awarded)) return 'awarded';
    // Firm (negotiated) offer -> Quoted; indicative listing (search_only) -> Listing.
    if (q.some((x) => x.firm) || t?.state === 'quoted') return 'quoted';
    if (q.length) return 'listed';
    if (t?.needs_human) return 'attention';
    if (t?.state === 'awaiting_us') return 'replied';
    if (t) return 'contacted';
    if (c.status === 'accepted') return 'shortlisted';
    return 'discovered';
  }, [threads, quotesBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let on = true;
    forgeAPI.getQuotes(tenderId).then((data) => {
      const list = data.quotes || data || []; const by = {};
      list.forEach((q) => { const k = q.supplier_id || q.candidate_id || q.supplier_name; if (k) (by[k] = by[k] || []).push(q); });
      if (on) setQuotesBy(by);
    }).catch(() => {});
    return () => { on = false; };
  }, [tenderId, summary?.counts?.quotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Evidence docs Accio stored from Alibaba (certs, spec sheets, quote PDFs, images).
  useEffect(() => {
    let on = true;
    forgeAPI.getSupplierDocuments(tenderId).then((data) => {
      const by = {};
      (data.documents || data || []).forEach((d) => {
        const k = d.candidate_id; if (k) (by[k] = by[k] || []).push(d);
      });
      if (on) setDocsBy(by);
    }).catch(() => {});
    return () => { on = false; };
  }, [tenderId, summary?.counts?.candidates, summary?.counts?.quotes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!selectedId && candidates.length) setSelectedId(candidates[0].candidate_id); }, [candidates, selectedId]);

  useEffect(() => {
    if (!autopilot) return undefined;
    const id = setInterval(() => { forgeAPI.pollSourcing(tenderId).then(refresh).catch(() => {}); }, 25000);
    return () => clearInterval(id);
  }, [autopilot, tenderId, refresh]);

  const selected = candidates.find((c) => c.candidate_id === selectedId) || null;
  const selThread = threadFor(selected);
  const attachments = (selThread?.messages || [])
    .flatMap((m) => (m.attachments || []).map((a) => ({ ...a, dir: m.direction })));
  useEffect(() => { threadEnd.current?.scrollIntoView({ block: 'end' }); }, [selThread?.message_count, selectedId]);
  useEffect(() => {
    if (!selected) return;
    const cs = selected.contacts || {};
    setChannel(cs.email ? 'email' : cs.whatsapp ? 'whatsapp' : 'email');
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn) => { setBusy(true); try { await fn(); await refresh(); } finally { setBusy(false); } };
  const accept = (c) => run(() => forgeAPI.setCandidateStatus(c.candidate_id, 'accepted'));
  const reject = (c) => run(() => forgeAPI.setCandidateStatus(c.candidate_id, 'rejected'));
  const reachOut = (c) => run(() => forgeAPI.openConversation(c.candidate_id));
  const sendMsg = async () => { if (!selThread || !draft.trim()) return; const b = draft.trim(); setDraft(''); await run(() => forgeAPI.sendMessage(selThread.thread_id, b, channel)); };
  const aiReply = () => selThread && run(() => forgeAPI.nextTurn(selThread.thread_id));
  const simReply = async () => { if (!selThread) return; setSimBusy(true); try { await forgeAPI.simReply(selThread.thread_id, persona); await refresh(); } finally { setSimBusy(false); } };
  const simRun = async () => { if (!selThread) return; setSimBusy(true); try { await forgeAPI.simRun(selThread.thread_id, 3, persona); await refresh(); } finally { setSimBusy(false); } };
  const escalate = () => selThread && run(() => forgeAPI.escalate(selThread.thread_id));
  const award = (q) => q?.quote_id && q?.line_item != null && run(() => forgeAPI.awardLineItem(tenderId, q.line_item, q.quote_id));
  const requestSample = () => selThread && run(() => forgeAPI.sendMessage(selThread.thread_id, 'Could you provide a sample for evaluation? Please advise sample cost and lead time.', channel, 'request_sample'));
  const saveToLibrary = (c) => run(() => forgeAPI.upsertSupplier({ company: c.company, country: c.country, city: c.city, contacts: c.contacts, certifications: c.certifications, stats: c.stats, source: c.source || 'alibaba', favorite: true }));
  const openDoc = async (docId) => {
    // Open the tab synchronously (inside the click gesture) so the popup blocker
    // doesn't kill it while we await the presigned URL; then point it at the file.
    const w = window.open('about:blank', '_blank');
    try {
      const url = await forgeAPI.getSupplierDocUrl(docId);
      if (url && w) w.location.href = url;
      else if (url) window.open(url, '_blank');
      else if (w) w.close();
    } catch { if (w) w.close(); }
  };

  const discover = async () => {
    const products = [...new Set(specLines.map((l) => l.product_name).filter(Boolean))];
    if (!products.length) { setDiscoverErr('Build the spec sheet first.'); return; }
    setDiscovering(true); setDiscoverErr(null);
    try {
      const r = await Promise.allSettled(products.slice(0, 3).map((p) => forgeAPI.discover(tenderId, p, 8)));
      await refresh();
      if (r.every((x) => x.status === 'rejected')) setDiscoverErr('Discovery failed (timeout / Apify key).');
    } finally { setDiscovering(false); }
  };
  const addManual = async () => {
    if (!form.company) return;
    await forgeAPI.addCandidate(tenderId, { company: form.company, country: form.country, product_name: specLines.map((l) => l.product_name).join(' + ') || 'Product', line_items: specLines.map((l) => l.line_item), contacts: form.email ? { email: form.email } : undefined });
    setForm({ company: '', country: '', email: '' }); setAdding(false); refresh();
  };
  const onCsv = async (e) => {
    const file = e.target.files && e.target.files[0]; e.target.value = '';
    if (!file) return;
    const text = await file.text();
    await run(async () => {
      const r = await forgeAPI.importVendors(tenderId, text, specLines.map((l) => l.product_name).join(' + '));
      setDiscoverErr(r && r.created != null ? null : 'CSV import failed');
      if (r && r.created != null) setDiscoverErr(`Imported ${r.created} vendor${r.created === 1 ? '' : 's'}${r.skipped ? ` (${r.skipped} skipped)` : ''}.`);
    });
  };

  const groups = GROUP_ORDER.map((k) => [k, candidates.filter((c) => statusOf(c) === k)]).filter(([, l]) => l.length);
  // Only channels we can actually auto-send on. Alibaba has no buyer API, so it
  // is NOT a send channel — its storefront shows as a reference link instead.
  const availChannels = (c) => { const cs = c?.contacts || {}; const out = []; if (cs.email) out.push('email'); if (cs.whatsapp) out.push('whatsapp'); return out; };
  const website = (c) => { const w = c?.contacts?.website; if (!w) return null; return w.startsWith('http') ? w : `https://${w}`; };

  return (
    <div className="forge-cc">
      {/* top bar */}
      <div className="forge-spec-toolbar">
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="forge-btn-ghost" onClick={() => setAdding((a) => !a)}><Plus size={14} /> Add</button>
          <label className="forge-btn-ghost" style={{ cursor: 'pointer' }} title="Bulk-import vendors from CSV (columns: company, email, country, product, whatsapp, website)">
            <Upload size={14} /> Upload CSV
            <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onCsv} />
          </label>
        </div>
        <span className="forge-muted" style={{ fontSize: 12 }}>Suppliers are sourced + negotiated by Accio</span>
      </div>
      {adding && (
        <div className="forge-add-row">
          <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <button className="forge-btn-sm" onClick={addManual}>Add</button>
        </div>
      )}
      {busy && <div className="forge-progress" />}
      {busy && <div className="forge-muted" style={{ fontSize: 12 }}>Working…</div>}
      {discoverErr && <div style={{ color: '#b91c1c', fontSize: 13 }}>{discoverErr}</div>}

      <div className="forge-cc-grid">
        {/* LEFT — pipeline rail */}
        <div className="forge-cc-pane forge-cc-scroll">
          {candidates.length === 0 && <div className="forge-muted" style={{ padding: 14, fontSize: 13 }}>No suppliers yet — click <b>⚡ Source</b> (top right) to have Accio find compliant suppliers, or <b>Add</b> one manually.</div>}
          {groups.map(([k, list]) => (
            <div key={k}>
              <div className="forge-cc-grouphdr" style={{ color: STATUS[k].color }}>{STATUS[k].label} · {list.length}</div>
              {list.map((c) => {
                const t = threadFor(c);
                // Scannable status: the live negotiation state (or the candidate
                // stage if no thread), the best quote price, and the message count.
                const [sLabel, sColor] = t
                  ? (STATE_LABEL[t.state] || [t.state || 'In progress', '#6b7785'])
                  : [STATUS[k].label, STATUS[k].color];
                const prices = quoteRows(c).map((r) => r.unit_price).filter((v) => v != null && !Number.isNaN(Number(v)));
                const best = prices.length ? Math.min(...prices.map(Number)) : null;
                const cur = quoteRows(c)[0]?.currency || 'USD';
                const priceStr = best != null ? (cur === 'USD' ? `$${best}` : `${best} ${cur}`) : null;
                return (
                  <button key={c.candidate_id} className={`forge-cc-item ${c.candidate_id === selectedId ? 'sel' : ''}`}
                    style={{ borderLeftColor: c.candidate_id === selectedId ? sColor : 'transparent' }}
                    onClick={() => setSelectedId(c.candidate_id)}>
                    <span className="forge-cc-avatar" style={{ background: avColor(c.company) }}>{initials(c.company)}</span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'block', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.company}</span>
                      <span className="forge-muted" style={{ fontSize: 11, display: 'flex', gap: 5, alignItems: 'center', minWidth: 0 }}>
                        <span className="forge-cc-dot" style={{ background: sColor, flexShrink: 0 }} />
                        <span style={{ color: sColor, fontWeight: 600, whiteSpace: 'nowrap' }}>{sLabel}</span>
                        {priceStr && <span style={{ flexShrink: 0 }}>· {priceStr}</span>}
                        {t && t.message_count ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>· <ChanIcon ch={t.channel} size={10} />{t.message_count}</span> : null}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* CENTER — unified conversation */}
        <div className="forge-cc-pane">
          {!selected ? <div className="forge-muted" style={{ padding: 20 }}>Select a supplier.</div> : (
            <>
              <div className="forge-cc-head">
                <div style={{ display: 'flex', gap: 9, alignItems: 'center', minWidth: 0 }}>
                  <span className="forge-cc-avatar" style={{ background: avColor(selected.company) }}>{initials(selected.company)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected.company}</div>
                    <div className="forge-muted" style={{ fontSize: 12 }}>
                      {selThread ? <>unified · {availChannels(selected).map((ch) => <span key={ch} style={{ marginRight: 5 }}><ChanIcon ch={ch} size={11} /></span>)}</> : 'no conversation yet'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {selThread?.conversation_url && (
                    <a className="forge-cc-link" href={selThread.conversation_url} target="_blank" rel="noreferrer"
                       title="Open the live Alibaba negotiation" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <Globe size={12} /> Alibaba chat <ExternalLink size={10} />
                    </a>
                  )}
                  <span className="forge-rel" style={{ background: STATUS[statusOf(selected)].color + '22', color: STATUS[statusOf(selected)].color }}>{STATUS[statusOf(selected)].label}</span>
                </div>
              </div>

              <div className="forge-cc-msgs forge-cc-scroll">
                {!selThread && (
                  <div className="forge-muted" style={{ textAlign: 'center', marginTop: 40 }}>
                    {statusOf(selected) === 'discovered'
                      ? <>Not shortlisted yet.<br /><button className="forge-btn-sm forge-btn-ok" disabled={busy} onClick={() => accept(selected)} style={{ marginTop: 8 }}>{busy ? <><Loader2 size={13} className="forge-spin" /> Accepting…</> : 'Accept'}</button></>
                      : <>Shortlisted — start the conversation.<br /><button className="forge-btn-sm forge-btn-primary" disabled={busy} onClick={() => reachOut(selected)} style={{ marginTop: 8 }}>{busy ? <><Loader2 size={13} className="forge-spin" /> Reaching out…</> : 'Reach out'}</button></>}
                  </div>
                )}
                {/* Accio negotiates on Alibaba and may report only a gist + link rather
                    than the full transcript — surface both at the top of the thread so
                    the operator can read the summary and jump into the real chat. */}
                {selThread && (selThread.summary || selThread.conversation_url) && (
                  <div className="forge-cc-accio-note">
                    <div className="forge-cc-accio-note-hd">
                      <span><Globe size={12} /> Accio negotiation on Alibaba</span>
                      {selThread.conversation_url && (
                        <a className="forge-cc-link" href={selThread.conversation_url} target="_blank" rel="noreferrer">
                          Open full chat <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    {selThread.summary && <div className="forge-cc-accio-note-body"><Markdown>{selThread.summary}</Markdown></div>}
                  </div>
                )}
                {(selThread?.messages || []).map((m) => {
                  const out = m.direction === 'outbound'; const agent = m.sender === 'agent';
                  return (
                    <div key={m.message_id} style={{ margin: '8px 0', textAlign: out ? 'right' : 'left' }}>
                      <div style={{ display: 'inline-block', maxWidth: '82%', textAlign: 'left' }}>
                        <div className="forge-muted" style={{ fontSize: 10, marginBottom: 2 }}>
                          <ChanIcon ch={m.channel} size={9} /> {agent ? '🤖 agent' : (out ? 'you' : (m.sender || 'supplier'))}{m.sent === false && out ? ' · draft' : ''}
                        </div>
                        <div className={`forge-cc-bubble ${agent ? 'forge-cc-bubble-agent' : out ? 'forge-cc-bubble-out' : 'forge-cc-bubble-in'}`}>
                          <Markdown>{m.body}</Markdown>
                          {m.lang && m.original_body && (
                            <details className="forge-cc-orig">
                              <summary>🌐 {m.direction === 'inbound' ? 'translated from' : 'sent in'} {m.lang} — show original</summary>
                              <Markdown>{m.original_body}</Markdown>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={threadEnd} />
              </div>

              {selThread && (
                <div className="forge-cc-composer">
                  {availChannels(selected).length === 0 ? (
                    <div className="forge-muted" style={{ fontSize: 12 }}>
                      No verified email/WhatsApp yet — enrich contacts to message this supplier, or escalate.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 7 }}>
                        {availChannels(selected).map((ch) => (
                          <button key={ch} className={`forge-cc-chanbtn ${channel === ch ? 'on' : ''}`} onClick={() => setChannel(ch)}>
                            <ChanIcon ch={ch} size={12} /> {CHAN[ch].label}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input style={{ flex: 1 }} placeholder={`Message via ${CHAN[channel] ? CHAN[channel].label : 'Email'}…`} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMsg()} />
                        <button className="forge-btn-sm forge-btn-primary" onClick={sendMsg} disabled={busy}><Send size={13} /></button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT — manufacturer details + actions */}
        <div className="forge-cc-pane forge-cc-scroll" style={{ padding: 14 }}>
          {!selected ? <div className="forge-muted">—</div> : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ display: 'flex', gap: 9, minWidth: 0 }}>
                  <span className="forge-cc-avatar" style={{ background: avColor(selected.company), width: 34, height: 34 }}>{initials(selected.company)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{selected.company}</div>
                    <div className="forge-muted" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} /> {[selected.city, selected.country].filter(Boolean).join(', ') || '—'}</div>
                  </div>
                </div>
                <button title="Save to Supplier Library" onClick={() => saveToLibrary(selected)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Star size={17} color="#f5b301" /></button>
              </div>

              {/* Status + conversation summary — pushed by Accio via log_negotiation */}
              {selThread && (() => {
                const [label, color] = STATE_LABEL[selThread.state] || [selThread.state || 'In progress', '#6b7785'];
                return (
                  <div className="forge-cc-section" style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span className="forge-muted" style={{ fontSize: 12, fontWeight: 600 }}>Status</span>
                      <span className="forge-rel" style={{ background: `${color}22`, color }}>{label}</span>
                    </div>
                    <div className="forge-muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>
                      {selThread.summary || <span style={{ fontStyle: 'italic' }}>Accio hasn’t summarised this conversation yet.</span>}
                    </div>
                    {selThread.conversation_url && (
                      <a className="forge-btn-sm" href={selThread.conversation_url} target="_blank" rel="noreferrer"
                         style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                        <Globe size={13} /> View negotiation on Alibaba <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
                {selected.stats?.reliability != null && <span className="forge-rel forge-rel-high">rel {Math.round(selected.stats.reliability)}</span>}
                {selected.stats?.rating != null && <span className="forge-line-chip">{selected.stats.rating}★</span>}
                {selected.stats?.on_time_rating != null && <span className="forge-line-chip">{selected.stats.on_time_rating}% on-time</span>}
                {selected.stats?.years != null && <span className="forge-line-chip">{selected.stats.years} yrs</span>}
              </div>

              {/* what they make */}
              {(selected.main_products || selected.product_name) && (
                <div className="forge-cc-detail"><Package size={12} /> <b>Makes:</b> {selected.main_products || selected.product_name}</div>
              )}
              {website(selected) && (
                <div className="forge-cc-detail"><Globe size={12} /> <b>Website:</b> <a className="forge-cc-link" href={website(selected)} target="_blank" rel="noreferrer">{selected.contacts.website} <ExternalLink size={10} /></a></div>
              )}
              {selected.contacts?.form_url && (
                <div className="forge-cc-detail"><Globe size={12} /> <b>Alibaba:</b> <a className="forge-cc-link" href={selected.contacts.form_url} target="_blank" rel="noreferrer">storefront <ExternalLink size={10} /></a> <span className="forge-muted">(message manually)</span></div>
              )}
              {(selected.area || selected.staff) && (
                <div className="forge-cc-detail"><UsersIcon size={12} /> <b>Factory:</b> {[selected.area && `${selected.area} m²`, selected.staff && `${selected.staff} staff`].filter(Boolean).join(' · ')}</div>
              )}
              {selected.contacts?.email && <div className="forge-cc-detail"><Mail size={12} /> {selected.contacts.email}{selected.contacts.email_source === 'pattern_inferred' ? <span className="forge-muted"> (inferred)</span> : ''}</div>}
              {selected.contacts?.whatsapp && <div className="forge-cc-detail"><MessageCircle size={12} /> {selected.contacts.whatsapp}</div>}

              {(selected.certifications || []).length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                  {selected.certifications.map((c) => <span key={c} className="forge-line-chip">{c}</span>)}
                </div>
              )}

              {(() => {
                const evidence = docsBy[selected.candidate_id] || [];
                if (!selThread && evidence.length === 0) return null;
                return (
                  <div className="forge-cc-section">
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                      Documents &amp; evidence ({attachments.length + evidence.length})
                    </div>
                    {/* Audit evidence Accio pulled from Alibaba (certs, quotes, spec sheets) */}
                    {evidence.map((d) => (
                      <button key={d.doc_id} type="button" onClick={() => openDoc(d.doc_id)}
                        className="forge-cc-detail forge-cc-link"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        title={`${d.kind} · ${(d.bytes || 0).toLocaleString()} bytes${d.source_url ? ` · from ${d.source_url}` : ''}`}>
                        <Paperclip size={12} /> {d.name || d.kind}
                        <span className="forge-line-chip" style={{ fontSize: 9 }}>{d.kind}</span>
                        <ExternalLink size={10} style={{ marginLeft: 'auto' }} />
                      </button>
                    ))}
                    {/* Files exchanged over the conversation thread */}
                    {attachments.map((a, i) => (
                      <a key={`att-${i}`} href={a.url || '#'} target="_blank" rel="noreferrer"
                        className="forge-cc-detail forge-cc-link"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Paperclip size={12} /> {a.filename}
                        <span className="forge-muted" style={{ fontSize: 10 }}>({a.dir === 'outbound' ? 'sent' : 'received'})</span>
                      </a>
                    ))}
                    {selThread && attachments.length + evidence.length === 0 && (
                      <div className="forge-muted" style={{ fontSize: 12 }}>None yet — certificates, quotes and spec sheets Accio pulls from Alibaba appear here.</div>
                    )}
                  </div>
                );
              })()}

              {quoteRows(selected).length > 0 && (
                <div className="forge-cc-section">
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Quotes (auto-extracted)</div>
                  {quoteRows(selected).map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, margin: '3px 0' }}>
                      <span className="forge-muted" style={{ flex: 1 }}>
                        {r.unit_price != null ? `${r.currency && r.currency !== 'USD' ? r.unit_price + ' ' + r.currency : '$' + r.unit_price}` : '—'}
                        {r.quantity != null ? ` @ ${r.quantity}` : ''} · MOQ {r.moq ?? '—'}{r.lead != null ? ` · ${r.lead}wk` : ''}{r.line_item != null ? ` · #${r.line_item}` : ''}
                      </span>
                      {r.quote_id && r.line_item != null && (r.awarded
                        ? <span className="forge-rel forge-rel-high">Awarded</span>
                        : <button className="forge-btn-sm forge-btn-ok" disabled={busy} onClick={() => award(r)}>Award</button>)}
                    </div>
                  ))}
                </div>
              )}

              <div className="forge-cc-section" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {selThread && <button className="forge-btn-sm" disabled={busy} onClick={requestSample}>Request sample</button>}
                {selThread && <button className="forge-btn-sm forge-btn-no" disabled={busy} onClick={escalate}>Escalate to human</button>}
                {!['rejected', 'discovered'].includes(statusOf(selected)) && <button className="forge-btn-sm forge-btn-no" disabled={busy} onClick={() => reject(selected)}>Reject supplier</button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourcingPanel;
